import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapPin, Phone, MessageCircle } from 'lucide-react';

const Location = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const address = '19 G2, Phase 4, Bahria Orchard, Raiwind Road, Lahore';
  const googleMapsUrl = `https://www.google.com/maps?q=${encodeURIComponent(address)}`;
  const embedUrl = `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`;

  return (
    <section
      id="location"
      ref={ref}
      className="py-20 md:py-32 bg-black"
      data-testid="location-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2
            className="font-cinzel text-4xl md:text-5xl font-semibold text-gold mb-4 tracking-wide"
            data-testid="location-title"
          >
            Find Us
          </h2>
          <div className="w-24 h-1 bg-gold mx-auto"></div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Map */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative h-[400px] rounded-sm overflow-hidden border border-gold/30"
            data-testid="location-map"
          >
            <iframe
              src={embedUrl}
              width="100%"
              height="100%"
              style={{ border: 0, filter: 'grayscale(100%) invert(90%) contrast(90%)' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Centurio Prime Location"
            ></iframe>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col justify-center space-y-8"
          >
            {/* Address */}
            <div className="flex items-start gap-4" data-testid="location-address">
              <div className="w-12 h-12 rounded-full border border-gold flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-gold" />
              </div>
              <div>
                <h3 className="font-cinzel text-xl text-gold mb-2">Address</h3>
                <p className="font-montserrat text-neutral-300 leading-relaxed">
                  19 G2, Phase 4, Bahria Orchard<br />
                  Raiwind Road, Lahore
                </p>
                <a
                  href={googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-3 text-gold hover:text-gold-hover font-montserrat text-sm uppercase tracking-wider transition-colors"
                  data-testid="location-directions-link"
                >
                  Get Directions →
                </a>
              </div>
            </div>

            {/* Phone */}
            <div className="flex items-start gap-4" data-testid="location-phone">
              <div className="w-12 h-12 rounded-full border border-gold flex items-center justify-center flex-shrink-0">
                <Phone className="w-6 h-6 text-gold" />
              </div>
              <div>
                <h3 className="font-cinzel text-xl text-gold mb-2">Phone</h3>
                <a
                  href="tel:+923016697234"
                  className="font-montserrat text-neutral-300 hover:text-gold transition-colors text-lg"
                >
                  0301-6697234
                </a>
              </div>
            </div>

            {/* WhatsApp */}
            <div className="flex items-start gap-4" data-testid="location-whatsapp">
              <div className="w-12 h-12 rounded-full border border-gold flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-6 h-6 text-gold" />
              </div>
              <div>
                <h3 className="font-cinzel text-xl text-gold mb-2">WhatsApp</h3>
                <a
                  href="https://wa.me/923016697234"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-montserrat text-neutral-300 hover:text-gold transition-colors text-lg"
                >
                  0301-6697234
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Location;
